import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { useQuestionnaire } from '../../lib/QuestionnaireContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/Card';
import AdminQuestionnaires from './AdminQuestionnaires';
import { createTestCompletedQuestionnaire } from '../../lib/firebase';
import FirebaseDebug from '../FirebaseDebug';
import { Skeleton } from '../ui/Skeleton';
import QuestionnaireStarter from './QuestionnaireStarter';

const QuestionnaireHome: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { userQuestionnaires, loading, createQuestionnaire, deleteQuestionnaire, loadUserQuestionnaires, isAdmin, loadAllCompletedQuestionnaires, dashboardSettings, loadDashboardSettings } = useQuestionnaire();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [creatingTestData, setCreatingTestData] = useState(false);
  const [showStarter, setShowStarter] = useState(false);

  // Reload user questionnaires and dashboard settings when component mounts
  useEffect(() => {
    const loadQuestionnaires = async () => {
      try {
        await loadUserQuestionnaires();
      } catch (error) {
        console.error('Error loading questionnaires:', error);
      }
    };

    const loadSettings = async () => {
      try {
        await loadDashboardSettings();
      } catch (error) {
        console.error('Error loading dashboard settings:', error);
      }
    };

    loadQuestionnaires();
    loadSettings();
  }, [loadUserQuestionnaires, loadDashboardSettings]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const handleShowQuestionnaireStarter = () => {
    setShowStarter(true);
  };

  const handleStartNewQuestionnaire = async () => {
    try {
      setCreating(true);
      const questionnaireId = await createQuestionnaire();
      
      // Navigate directly to the questionnaire form
      setTimeout(() => {
        navigate(`/questionnaire/form/${questionnaireId}`);
      }, 500);
    } catch (error) {
      console.error('Error creating questionnaire:', error);
      setCreating(false);
    }
  };

  const handleContinueQuestionnaire = (id: string) => {
    // For completed questionnaires, navigate to the first step
    const questionnaire = userQuestionnaires.find(q => q.id === id);
    if (!questionnaire) {
      console.error('Questionnaire not found:', id);
      return;
    }
    
    // Ensure we have a valid step number
    const step = questionnaire.isComplete ? 0 : Math.max(0, questionnaire?.currentStep || 0);
    
    console.log(`Continuing questionnaire ${id} at step ${step}`);
    
    // Use replace: true to avoid adding to browser history, which can help prevent loops
    navigate(`/questionnaire/form/${id}?step=${step}`, { replace: true });
  };

  const handleDeleteQuestionnaire = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this questionnaire? This action cannot be undone.')) {
      try {
        setDeleting(id);
        await deleteQuestionnaire(id);
        await loadUserQuestionnaires();
      } catch (error) {
        console.error('Error deleting questionnaire:', error);
      } finally {
        setDeleting(null);
      }
    }
  };

  const handleCreateTestData = async () => {
    if (!isAdmin) return;
    
    try {
      setCreatingTestData(true);
      await createTestCompletedQuestionnaire();
      // Reload all completed questionnaires
      await loadAllCompletedQuestionnaires();
      console.log("Test data created and questionnaires reloaded");
    } catch (error) {
      console.error('Error creating test data:', error);
    } finally {
      setCreatingTestData(false);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getProgressStatus = (questionnaire: any) => {
    if (questionnaire.isComplete) {
      return { status: 'Completed', color: 'bg-green-500' };
    }
    
    // Calculate progress based on current step and total steps (now including blog, events, and split content management)
    const totalSteps = 14; // Updated from 13 to include the split content management steps
    const currentStep = questionnaire.currentStep || 0;
    
    // Ensure currentStep is valid (non-negative)
    const validStep = Math.max(0, currentStep);
    const progress = Math.floor((validStep / totalSteps) * 100);
    
    if (progress === 0) {
      return { status: 'Not started', color: 'bg-gray-300' };
    } else if (progress < 33) {
      return { status: `In progress (${progress}%)`, color: 'bg-gray-100' };
    } else if (progress < 66) {
      return { status: `In progress (${progress}%)`, color: 'bg-gray-300' };
    } else if (progress < 100) {
      return { status: `In progress (${progress}%)`, color: 'bg-gray-400' };
    } else {
      return { status: 'Ready to submit', color: 'bg-gray-500' };
    }
  };

  // Render skeleton cards for loading state
  const renderSkeletonCards = () => {
    return Array(2).fill(0).map((_, index) => (
      <Card key={`skeleton-${index}`} className="overflow-hidden">
        <div className="h-2 w-full bg-gray-200"></div>
        <CardContent className="pt-6">
          <div className="flex justify-between items-start mb-4">
            <div className="w-full">
              <Skeleton className="h-5 w-3/4 mb-2" />
              <div className="flex items-center mt-1">
                <Skeleton className="w-2 h-2 rounded-full mr-2" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-4 w-40 mt-2" />
              <Skeleton className="h-4 w-48 mt-1" />
            </div>
          </div>
          <div className="text-sm">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-28" />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between pt-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-16" />
        </CardFooter>
      </Card>
    ));
  };

  return (
    <div className="flex-1 bg-background">
      <div className="container max-w-5xl mx-auto p-4">
        
        {/* Admin Panel - Only shown for admin users */}
        {isAdmin && (
          <div className="mb-8">
            <AdminQuestionnaires />
          </div>
        )}

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {renderSkeletonCards()}
          </div>
        ) : showStarter ? (
          // Show QuestionnaireStarter when the user clicks "Start New Questionnaire"
          <QuestionnaireStarter onCancel={() => setShowStarter(false)} />
        ) : userQuestionnaires.length === 0 ? (
          // Show QuestionnaireStarter when no questionnaires exist
          <QuestionnaireStarter />
        ) : (
          // Show existing questionnaires when they exist
          <>
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>
                  {dashboardSettings?.welcomeTitle || 'Welcome to the Questionnaire'}
                </CardTitle>
                <CardDescription>
                  {dashboardSettings?.welcomeDescription || 
                   'Please fill out the questionnaire to the best of your knowledge. We\'ve broken it down into smaller sections to make it easier to complete. We will use your responses to inform the scope of our proposal. Please note that any major deviations (additional pages, functionalities, or integrations) that are not mentioned could result in an additional estimate.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <Button 
                    variant="default" 
                    onClick={handleStartNewQuestionnaire}
                    disabled={creating || loading}
                  >
                    {creating ? 'Creating...' : (dashboardSettings?.startButtonText || 'Start New Questionnaire')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Your Questionnaires</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {userQuestionnaires.map((questionnaire) => {
                  const { status, color } = getProgressStatus(questionnaire);
                  return (
                    <Card key={questionnaire.id} className="overflow-hidden">
                      <div className={`h-2 w-full ${color}`}></div>
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-medium">
                              {questionnaire.formData.company || questionnaire.formData.name || 'Untitled Questionnaire'}
                            </h3>
                            <div className="flex items-center mt-1">
                              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${color}`}></span>
                              <span className="text-sm font-medium">{status}</span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Created: {formatDate(questionnaire.createdAt)}
                            </p>
                            {questionnaire.updatedAt && (
                              <p className="text-sm text-muted-foreground">
                                Last updated: {formatDate(questionnaire.updatedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-sm">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            {questionnaire.formData.name && (
                              <>
                                <span className="text-muted-foreground">Name:</span>
                                <span>{questionnaire.formData.name}</span>
                              </>
                            )}
                            {questionnaire.formData.company && (
                              <>
                                <span className="text-muted-foreground">Company:</span>
                                <span>{questionnaire.formData.company}</span>
                              </>
                            )}
                            {questionnaire.formData.email && (
                              <>
                                <span className="text-muted-foreground">Email:</span>
                                <span>{questionnaire.formData.email}</span>
                              </>
                            )}
                            {questionnaire.formData.projectType && (
                              <>
                                <span className="text-muted-foreground">Project Type:</span>
                                <span>{questionnaire.formData.projectType}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                      <CardFooter className="flex justify-between pt-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteQuestionnaire(questionnaire.id)}
                          disabled={deleting === questionnaire.id}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          {deleting === questionnaire.id ? 'Deleting...' : 'Delete'}
                        </Button>
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={() => handleContinueQuestionnaire(questionnaire.id)}
                        >
                          {questionnaire.isComplete ? 'Edit' : 'Continue'}
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionnaireHome; 